import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  Renderer2,
} from "@angular/core";

@Directive({
  selector: "[coreUiTooltip]",
  standalone: false,
})
export class CoreUiTooltipDirective {
  @Input("coreUiTooltip") text = "";

  @Input() tooltipTitle?: string;

  @Input() tooltipDescription?: string;

  @Input() tooltipDescriptionHtml?: string;

  @Input() tooltipHighlight?: string;

  @Input() tooltipMaxWidth: string = "390px";

  @Input()
  tooltipPosition: "top" | "bottom" | "left" | "right" = "top";

  private tooltipEl?: HTMLElement;

  constructor(
    private host: ElementRef<HTMLElement>,
    private renderer: Renderer2,
  ) {}

  @HostListener("mouseenter")
  @HostListener("focusin")
  show() {
    if (
      !this.text &&
      !this.tooltipTitle &&
      !this.tooltipDescription &&
      !this.tooltipDescriptionHtml &&
      !this.tooltipHighlight
    ) {
      return;
    }

    if (this.tooltipEl) {
      return;
    }

    const hasContent = !!(
      this.text ||
      this.tooltipTitle ||
      this.tooltipDescription ||
      this.tooltipDescriptionHtml ||
      this.tooltipHighlight
    );

    if (!hasContent) {
      return;
    }

    const tooltip =
      this.renderer.createElement("div");

    this.renderer.addClass(
      tooltip,
      "core-ui-tooltip",
    );

    this.renderer.addClass(
      tooltip,
      "core-ui-tooltip--card",
    );

    this.renderer.addClass(
      tooltip,
      `core-ui-tooltip--${this.tooltipPosition}`,
    );

    this.renderer.setStyle(
      tooltip,
      "width",
      this.tooltipMaxWidth,
    );

    this.renderer.setStyle(
      tooltip,
      "min-width",
      "240px",
    );

    const contentWrapper =
      this.renderer.createElement("div");

    this.renderer.addClass(
      contentWrapper,
      "core-ui-tooltip__content",
    );

    this.renderer.appendChild(
      tooltip,
      contentWrapper,
    );

    const textColumn =
      this.renderer.createElement("div");

    this.renderer.addClass(
      textColumn,
      "core-ui-tooltip__text-column",
    );

    /* =====================================================
       TÍTULO
       ===================================================== */

    if (this.tooltipTitle) {
      const title =
        this.renderer.createElement("div");

      this.renderer.addClass(
        title,
        "core-ui-tooltip__title",
      );

      title.textContent =
        this.tooltipTitle;

      this.renderer.appendChild(
        textColumn,
        title,
      );
    }

    /* =====================================================
       DESCRIPCIÓN CON HTML
       ===================================================== */

    if (this.tooltipDescriptionHtml) {
      const desc =
        this.renderer.createElement("div");

      this.renderer.addClass(
        desc,
        "core-ui-tooltip__description",
      );

      this.renderer.setProperty(
        desc,
        "innerHTML",
        this.tooltipDescriptionHtml,
      );

      this.renderer.appendChild(
        textColumn,
        desc,
      );
    }

    /* =====================================================
       DESCRIPCIÓN NORMAL

       Se resaltan automáticamente:

       USD
       PEN
       banco
       sistema.
       ===================================================== */

    else if (this.tooltipDescription) {
      const desc =
        this.renderer.createElement("div");

      this.renderer.addClass(
        desc,
        "core-ui-tooltip__description",
      );

      const formattedDescription =
        this.formatDescription(
          this.tooltipDescription,
        );

      this.renderer.setProperty(
        desc,
        "innerHTML",
        formattedDescription,
      );

      this.renderer.appendChild(
        textColumn,
        desc,
      );
    }

    /* =====================================================
       HIGHLIGHT
       ===================================================== */

    if (this.tooltipHighlight) {
      const highlight =
        this.renderer.createElement("div");

      this.renderer.addClass(
        highlight,
        "core-ui-tooltip__highlight",
      );

      const span =
        this.renderer.createElement("span");

      this.renderer.addClass(
        span,
        "core-ui-tooltip__highlight-text",
      );

      span.textContent =
        this.tooltipHighlight;

      this.renderer.appendChild(
        highlight,
        span,
      );

      this.renderer.appendChild(
        textColumn,
        highlight,
      );
    }

    /* =====================================================
       TEXTO SIMPLE
       ===================================================== */

    if (
      this.text &&
      !this.tooltipTitle &&
      !this.tooltipDescription &&
      !this.tooltipDescriptionHtml &&
      !this.tooltipHighlight
    ) {
      textColumn.textContent =
        this.text;
    }

    this.renderer.appendChild(
      contentWrapper,
      textColumn,
    );

    /* =====================================================
       BOTÓN CERRAR
       ===================================================== */

    const closeBtn =
      this.renderer.createElement("button");

    this.renderer.addClass(
      closeBtn,
      "core-ui-tooltip__close",
    );

    this.renderer.setAttribute(
      closeBtn,
      "type",
      "button",
    );

    this.renderer.setAttribute(
      closeBtn,
      "aria-label",
      "Cerrar tooltip",
    );

    const svgClose = `
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg">

        <path
          d="M18 6L6 18"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"/>

        <path
          d="M6 6l12 12"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"/>

      </svg>
    `;

    this.renderer.setProperty(
      closeBtn,
      "innerHTML",
      svgClose,
    );

    this.renderer.listen(
      closeBtn,
      "click",
      () => this.hide(),
    );

    this.renderer.appendChild(
      contentWrapper,
      closeBtn,
    );

    /* =====================================================
       AGREGAR TOOLTIP AL BODY
       ===================================================== */

    this.renderer.appendChild(
      document.body,
      tooltip,
    );

    this.tooltipEl = tooltip;

    void tooltip.offsetHeight;

    /* =====================================================
       POSICIONAMIENTO
       ===================================================== */

    const updatePosition = () => {
      if (!this.tooltipEl) {
        return;
      }

      const hostEl =
        this.host.nativeElement;

      const iconEl =
        (hostEl.querySelector(
          "img, svg",
        ) as HTMLElement) ?? hostEl;

      const targetRect =
        iconEl.getBoundingClientRect();

      const tooltipRect =
        this.tooltipEl.getBoundingClientRect();

      const iconCenterX =
        targetRect.left +
        targetRect.width / 2;

      const ARROW_RIGHT_OFFSET = 17;

      const ARROW_HALF_WIDTH = 10;

      const distanceFromRightToTip =
        ARROW_RIGHT_OFFSET +
        ARROW_HALF_WIDTH;

      let left =
        iconCenterX -
        (
          tooltipRect.width -
          distanceFromRightToTip
        );

      const top =
        targetRect.top -
        tooltipRect.height -
        18;

      left = Math.round(left);

      const roundedTop =
        Math.round(top);

      this.renderer.setStyle(
        this.tooltipEl,
        "transform",
        `translate(${left}px, ${roundedTop}px)`,
      );

      this.renderer.setStyle(
        this.tooltipEl,
        "left",
        "0",
      );

      this.renderer.setStyle(
        this.tooltipEl,
        "top",
        "0",
      );
    };

    updatePosition();

    /* =====================================================
       REPOSICIONAMIENTO
       ===================================================== */

    const repositionListener = () =>
      requestAnimationFrame(
        updatePosition,
      );

    window.addEventListener(
      "scroll",
      repositionListener,
      true,
    );

    window.addEventListener(
      "resize",
      repositionListener,
    );

    (this.tooltipEl as any)
      ._repositionListener =
      repositionListener;

    /* =====================================================
       MOSTRAR
       ===================================================== */

    this.renderer.addClass(
      tooltip,
      "core-ui-tooltip--visible",
    );

    this.renderer.setStyle(
      this.host.nativeElement,
      "cursor",
      "pointer",
    );
  }

  /* =======================================================
     FORMATEAR DESCRIPCIÓN

     Convierte automáticamente:

     USD       → negrita
     PEN       → negrita
     banco     → negrita
     sistema.  → negrita
     ======================================================= */

  private formatDescription(
    description: string,
  ): string {
    return description
      .replace(
        /\b(USD|PEN)\b/g,
        "<strong>$1</strong>",
      )
      .replace(
        /\b(banco)\b/gi,
        "<strong>$1</strong>",
      )
      .replace(
        /\b(sistema)\./gi,
        "<strong>$1.</strong>",
      );
  }

  /* =====================================================
     OCULTAR
     ===================================================== */

  @HostListener("mouseleave")
  @HostListener("focusout")
  hide() {
    if (this.tooltipEl) {
      const listener =
        (this.tooltipEl as any)
          ._repositionListener;

      if (listener) {
        window.removeEventListener(
          "scroll",
          listener,
          true,
        );

        window.removeEventListener(
          "resize",
          listener,
        );
      }

      this.renderer.removeChild(
        document.body,
        this.tooltipEl,
      );

      this.tooltipEl =
        undefined;

      this.renderer.removeStyle(
        this.host.nativeElement,
        "cursor",
      );
    }
  }
}